import { z } from 'zod';

import { guestClient } from './guestClient';

// Guest signed-URL endpoints (routes/web.php, no /api prefix).

// Backend showPaymentForm отдаёт ВЛОЖЕННЫЙ + {data}-wrapped объект
// (payment/property/owner/tenant). Раньше схема ждала плоский корень -> Zod
// падал на КАЖДОЙ валидной ссылке, и гость видел "ссылка устарела".
export const GuestPaymentZ = z.object({
  payment: z.object({
    id: z.number().int(),
    amount_expected: z.number(), // тенге, целое (НЕ decimal-строка)
    amount_paid: z.number(),
    period_month: z.number().int(),
    period_year: z.number().int(),
    due_date: z.string(),
    status: z.string(),
  }),
  property: z.object({ address: z.string(), city: z.string() }),
  owner: z.object({ name: z.string() }),
  tenant: z.object({ name: z.string() }),
});
export type GuestPayment = z.infer<typeof GuestPaymentZ>;

export async function fetchGuestPayment(id: number): Promise<GuestPayment> {
  const res = await guestClient.get(`/guest/pay/${id}`);
  return z.object({ data: GuestPaymentZ }).parse(res.data).data;
}

// Backend kaspiData отдаёт {data: KaspiQrCode::toArray()} =
// { qr_content, deep_link, invoice_id, expires_at }. qr_content — это СЫРОЙ
// payload для QR (URL/kaspi://...), НЕ картинка. Старая схема ждала qr_image +
// amount на корне -> Zod падал, вкладка Kaspi была мёртвой.
export const GuestKaspiZ = z.object({
  qr_content: z.string(),
  deep_link: z.string(),
  invoice_id: z.string().optional(),
  expires_at: z.string().nullable().optional(),
});

export async function fetchGuestKaspi(id: number): Promise<z.infer<typeof GuestKaspiZ>> {
  const res = await guestClient.get(`/guest/pay/${id}/kaspi`);
  return z.object({ data: GuestKaspiZ }).parse(res.data).data;
}

export async function uploadGuestProof(id: number, form: FormData): Promise<void> {
  await guestClient.post(`/guest/pay/${id}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// Readings

export async function submitGuestReading(
  leaseId: number,
  month: number,
  year: number,
  form: FormData,
): Promise<void> {
  await guestClient.post(`/guest/readings/${leaseId}/${month}/${year}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// Maintenance

export const GuestMaintenanceInfoZ = z.object({
  lease_id: z.number().int(),
  property_address: z.string(),
});

export async function fetchGuestMaintenanceInfo(
  leaseId: number,
): Promise<z.infer<typeof GuestMaintenanceInfoZ>> {
  const res = await guestClient.get(`/guest/maintenance/${leaseId}`);
  return GuestMaintenanceInfoZ.parse(res.data);
}

export async function createGuestMaintenance(leaseId: number, form: FormData): Promise<void> {
  await guestClient.post(`/guest/maintenance/${leaseId}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// Signing

export const GuestSignInfoZ = z.object({
  document_id: z.number().int(),
  pdf_url: z.string(),
  signing_status: z.string(),
});

export async function fetchGuestSignInfo(
  documentId: number,
): Promise<z.infer<typeof GuestSignInfoZ>> {
  const res = await guestClient.get(`/guest/sign/${documentId}`);
  return GuestSignInfoZ.parse(res.data);
}

export const GuestSignInitiateZ = z.object({
  qrCode: z.string(),
  operationId: z.string(),
  expire_at: z.string(),
});

export async function initiateGuestSign(
  documentId: number,
): Promise<z.infer<typeof GuestSignInitiateZ>> {
  const res = await guestClient.post(`/guest/sign/${documentId}`);
  return GuestSignInitiateZ.parse(res.data);
}

export const GuestSignStatusZ = z.object({
  status: z.enum(['waiting', 'done', 'canceled', 'fail', 'expired']),
  expire_at: z.string().optional(),
});

export async function pollGuestSignStatus(
  documentId: number,
): Promise<z.infer<typeof GuestSignStatusZ>> {
  const res = await guestClient.get(`/guest/sign/${documentId}/status`);
  return GuestSignStatusZ.parse(res.data);
}

export const guestApi = {
  payment: fetchGuestPayment,
  kaspi: fetchGuestKaspi,
  uploadProof: uploadGuestProof,
  submitReading: submitGuestReading,
  maintenanceInfo: fetchGuestMaintenanceInfo,
  createMaintenance: createGuestMaintenance,
  signInfo: fetchGuestSignInfo,
  initiateSign: initiateGuestSign,
  pollSignStatus: pollGuestSignStatus,
};
